/* eslint-disable @next/next/no-img-element */
import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/culinary-delights-around-the-world`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title=" A Food Lover's Paradise: Culinary Delights Around the World | DAYMADE"
        description="Check out DAYMADE's ultimate culinary travel guide. Let's go on a gastronomic journey as we explore some of the most delectable destinations around the world."
        openGraph={{
          title:
            "A Food Lover's Paradise: Culinary Delights Around the World | DAYMADE",
          description:
            "Check out DAYMADE's ultimate culinary travel guide. Let's go on a gastronomic journey as we explore some of the most delectable destinations around the world.",
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/culinary-delights-around-the-world',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Katz Delicatessen in New York',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'food,travel,daymade,win prizes',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Katz Delicatessen in New York"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            A Food Lover's Paradise: Culinary Delights Around the World
          </h1>
          <p className="subheading">
            Get ready to embark on a gastronomic journey as we explore some of
            the most delectable destinations around the world.
          </p>
          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />
          <p>
            Are you a food lover in search of the ultimate culinary adventure?
            Get ready to embark on a gastronomic journey as we explore some of
            the most delectable destinations around the world. From the bustling
            streets of New York to the exotic flavours of South East Asia, the
            rich heritage of Sicilian cuisine, the vibrant spices of Mexico, the
            tantalising flavours of the Philippines, the artistry of Japanese
            cuisine, the diverse flavours of Peru, and the mouthwatering
            delights of Portugal, these destinations will be epic for your taste
            buds and leave you craving more!
          </p>
          <h2>New York: The Melting Pot of Cuisine</h2>
          <p>
            <BlogLink internal="/prizes/details/luckytrip-nyc">
              New York
            </BlogLink>
            , or Nuu Yooorkkkkk (in your best Bronx accent), known as the
            melting pot of cultures, offers an irresistible array of culinary
            delights. From world-class restaurants to street food vendors, the
            city that never sleeps has it all. Indulge in a slice of authentic
            New York-style pizza (Joe's Pizza, if you know you know!), savour
            the flavours of a classic pastrami sandwich - Katz Deli is an
            institution, queueing compulsory - or treat yourself to a
            mouthwatering bagel with lox and cream cheese. Don't forget to
            explore the vibrant food scenes like Chelsea Market, Smorgasburg,
            Chinatown or East Harlem to explore Latino restaurants, where you
            can sample a variety of the world's cuisines.
          </p>
          <h2>Asia: A Feast for the Senses</h2>
          <p>
            <BlogLink internal="/prizes/details/eat-your-way-through-asia">
              Asia
            </BlogLink>{' '}
            is a paradise for food lovers, with its bold and aromatic flavours.
            From the fiery street food of Thailand and the rich curries of
            Malaysia to the vibrant noodle dishes of Vietnam and the savoury
            satay of Indonesia, this region offers a culinary adventure like no
            other. Dive into a bowl of steaming pho, sample the tangy flavours
            of pad Thai, or indulge in the fragrant spices of a nasi lemak.
            Don't forget to explore the bustling night markets, where you can
            savour a variety of delicious street food.
          </p>
          <h2>Sicily: A Taste of the Mediterranean</h2>
          <p>
            <BlogLink internal="/prizes/details/7-days-in-sicily">
              Sicily
            </BlogLink>
            , the largest island in the Mediterranean, is a treasure trove of
            culinary delights. With its fertile soil and proximity to the sea,
            the island boasts a rich and diverse cuisine. Sample the flavours of
            arancini, deep-fried rice balls filled with ragu or cheese, or
            indulge in a plate of fresh seafood pasta. Some of our favourite
            seafood joints include Here's an example of an{' '}
            <BlogLink external="https://sama-marzamemi.business.site/">
              SAMA Marzamemi
            </BlogLink>
            , Ristorante Le Stranizze &{' '}
            <BlogLink external="https://www.camuri.it/en/home-english/">
              Camùri Ristorante
            </BlogLink>
            . Don't miss out on the famous cannoli, a crispy pastry filled with
            sweet ricotta cream, or the refreshing flavours of granita, a
            semi-frozen dessert made with fresh fruit and sugar. And last but
            not least, don't skimp on the gelato during an evening's passeggiata
            (TikTok POV optional).
          </p>
          <h2>Mexico: Spices and Flavours Galore</h2>
          <p>
            <BlogLink internal="/prizes/details/mexico">Mexico</BlogLink> is
            renowned for its bold and vibrant cuisine, packed with spices and
            flavours. From the tangy ceviche and mouthwatering tacos to the rich
            mole and hearty tamales, Mexican cuisine is a true delight for the
            taste buds. Immerse yourself in the lively food markets like Mercado
            de San Juan in Mexico City or the bustling markets of Oaxaca.
            Prepare for a sensory overload as you sample a variety of
            traditional dishes that will take your taste buds on a wild ride.
            And of course, no visit to Mexico is complete without trying the
            refreshing flavours of a traditional margarita, sipping on some fine
            tequila or mezcal, and indulging in the decadent sweetness of
            Mexican chocolate. So grab your appetite and get ready to experience
            the vibrant flavours and rich culinary heritage of Mexico. It's a
            foodie's paradise that will leave you craving more! ¡Buen provecho!
          </p>
          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="food market in Mexico"
            className="singleBlogImage"
          />
          <h2>Philippines: A Fusion of Flavours</h2>
          <p>
            <BlogLink internal="/prizes/details/philippines">
              The Philippines
            </BlogLink>{' '}
            offers a unique fusion of flavours influenced by its rich history
            and diverse cultural heritage. Sample the flavours of adobo, a
            savoury and tangy meat dish marinated in vinegar and soy sauce, or
            indulge in the sweetness of leche flan, a caramel custard dessert.
            Don't miss out on the vibrant flavours of halo-halo, a refreshing
            dessert made with crushed ice, sweet beans, jellies, and fruits.
            Explore the local food stalls and markets, where you can try a
            variety of street food delicacies, including the Mercato Centrale in
            Manila, the largest food market in the country, or the Banchetto
            market.
          </p>
          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="filipino food"
            className="singleBlogImage"
          />
          <h2>Japan: Where Tradition Meets Innovation</h2>
          <p>
            <BlogLink internal="/prizes/details/japan">Japan</BlogLink> is a
            culinary paradise, where tradition meets innovation. From the
            delicate flavours of sushi and sashimi to the comforting warmth of
            ramen and the crispy perfection of tempura, Japanese cuisine is a
            true art form. Indulge in the flavours of yakitori, grilled skewered
            chicken, or savour the rich umami flavours of a bowl of miso soup.
            Don't forget to explore the local izakayas, where you can enjoy a
            variety of small plates and drinks in a lively atmosphere, maybe
            hitting up a listening bar afterwards, a hi-fi lovers paradise. In
            Tokyo, after visiting Tsukiji Tokyo's Central Fish Market, why not
            head for incredible sushi at Sushi Karaku in the Ginza District,
            with dishes like slow-simmered octopus with sweet plum sauce, a
            plate of edomae sushi with flounder, prawn, Otoro tuna, marinated
            tuna, and raw eel, plus beer and iced sake to drink.
          </p>
          <h2>Peru: A Fusion of Cultures</h2>
          <p>
            Peruvian cuisine is a fusion of flavours influenced by its diverse
            cultural heritage. From the zesty flavours of ceviche to the hearty
            and spicy flavours of aji de gallina, Peruvian cuisine offers a
            unique culinary experience. Sample the flavours of lomo saltado, a
            delicious stir-fry dish combining beef, onions, tomatoes, and french
            fries, or indulge in the sweet and creamy flavours of tres leches
            cake. Don't miss out on trying the national drink, Pisco Sour, a
            frothy cocktail made with Pisco brandy, lime juice, syrup, and egg
            white. Lima – the country's capital – has an unprecedented foodie
            scene, full of Michelin-star restaurants that make 50 Best Lists the
            world over. The Miraflores restaurant of chef Virgilio Martinez, who
            is also known for the Michelin-starred restaurant LIMA in London,
            Central has a steadfast position among the best restaurants in the
            world. Others include Astrid y Gaston, La Mar Cevichería Peruana,
            whose seasonal philosophy is "what the sea brings" or Maido,
            representing Lima's Nikkei cuisine - the fusion fare that resulted
            from the arrival of Japanese immigrants in{' '}
            <BlogLink internal="/prizes/details/peru">Peru</BlogLink>.
          </p>
          <h2>Portugal: Seafood and Pastries</h2>
          <p>
            <BlogLink internal="/prizes/details/weekend-break-in-melides-portugal">
              Portugal
            </BlogLink>{' '}
            is a food lover's paradise, especially for seafood enthusiasts. From
            the fresh and succulent grilled sardines to the flavorful bacalhau,
            a traditional salted cod dish, Portuguese cuisine is a delight for
            the senses. Indulge in the creamy and custardy goodness of pastel de
            nata, a traditional egg tart pastry, or savour the rich flavours of
            caldo verde, a hearty soup made with potatoes, kale, and chorizo.
            Don't forget to pair your meal with a glass of refreshing vinho
            verde or port, fortified wine produced in the Douro Valley. Saúde!
          </p>
          <Separator style={{ margin: '1em 0 2.5em' }} />
          <p>
            Embark on a culinary adventure around the world and satisfy your
            food cravings with these amazing destinations. From the diverse
            flavours of New York City to the exotic spices of South East Asia,
            the Mediterranean delights of Sicily, the bold flavours of Mexico,
            the fusion cuisine of the Philippines, the artistry of Japanese
            dishes, the diverse flavours of Peru, and the seafood and pastries
            of Portugal, each destination offers a unique culinary experience
            that will leave you craving for more. So pack your bags, prepare
            your taste buds, and get ready to indulge in some of the most
            delicious culinary delights the world has to offer!
          </p>

          <p>Bon appétit!</p>
          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
